import React from "react";
import {
	loadTranslations,
	setLocale,
	syncTranslationWithStore,
	Translate,
} from "react-redux-i18n";
import store from "assets/js/store";
import * as moment from "moment-timezone";
import Cookies from "universal-cookie";
import { setLanguage } from "assets/js/actions";
import originalBrand from "assets/js/scenes/brand/data/originalBrand";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { getUrlQueryParam } from "utils/urlUtils";
import PublicTranslations from "assets/js/translations/public";

const getLanguagesOptions = (selected) => {
	if (selected == "en") {
		selected = "en_US";
	}
	else if (selected == "fr") {
		selected = "fr_CA";
	}
	return languages().map((language, index) => {
		return {
			label: language.label,
			value: language.value,
			alternative: language.alternative,
			selected: language.alternative === selected || language.value === selected,
		};
	});
};
/**
 *
 * @param {locale value to get label for } locale
 * this function is used to get the labels displayed in the user settings, we need to add a try and catch block here
 * to handle the case if user's data in the database has a locale value that is not listed in the above
 * the case happened after removing PT
 */
const getLanguageLabel = (locale) => {
	try {
		return languages().find((language, index) => {
			return language.value == locale;
		}).label;
	} catch (error) {
		return languages().find(
			(language, index) =>
				language.value == adjustLocaleToMatchBE(fallBackLanguage)
		).label;
	}
};

/**
 * @return array
 */
function languages(languageList = undefined) {
	const defaultLocals = ["en_us", "fr_ca"];
	let resellerLocales = languageList || store?.getState()?.brand?.client_config?.languages;
	let localesToUse = [];

	if (resellerLocales && resellerLocales.length > 0) {
		resellerLocales = resellerLocales.map((locale) => locale.toLowerCase());
		localesToUse = supportedLocalesApi.filter((locale) => {
			return resellerLocales.includes(locale.value.toLowerCase());
		});
	} else {
		localesToUse = supportedLocalesApi.filter((locale) => {
			return defaultLocals.includes(locale.value.toLowerCase());
		});
	}
	//Some lists are created with API and the langue is not in the brand config.
	/*if(selected && !localesToUse.some(locale => locale.value === selected)){
		localesToUse.push(supportedLocalesApi.find(locale => locale.value === selected));
	}*/
	return localesToUse;
}

const getUrlsBasedOnLanguage = (urls, name, language = "en") => {
	let returnedUrl = "";

	if (urls) {
		if (urls[language]) returnedUrl = urls[language];
		else if (urls.en) returnedUrl = urls.en;
		else if (urls[Object.keys(urls)[0]]) returnedUrl = urls[Object.keys(urls)[0]];
	}

	if (returnedUrl === "default" && originalBrand?.url?.[name]) {
		return getUrlsBasedOnLanguage(originalBrand?.url?.[name], language);
	}

	return returnedUrl;
};

const fallBackLanguage = "en-US";
/** for some reason, need to check with BE guys, the backend has locales with - instead of _,
 *  for example : en_US instead of en-US
 *  while the standard is -, to solve the problem we replace all the - with -
 */
function adjustLocaleToMatchBE(locale) {
	return locale.replace("-", "_");
}
/**
 * the functions main purpose is handle how our fallback works in the system, priority is to user selection or cookie saved locale
 * if exists in our set of supported languages then navigator's language if supported, then en-US
 * @param userSelectedLocale locale
 */
function getLocaleWithFallback(locale, languageList = undefined) {
	const lookupLanguages = languages(languageList).map((lng) => lng.value);
	if (locale) {
		locale = adjustLocaleToMatchBE(locale);
		let foundLocale = lookupLanguages.find((lLang) => {
			if (locale == lLang)
				return true;
			else if (locale.substr(0, 2) === lLang)
				return true;
			return false;
		});
		// best match otherwise leave it to the rest
		if (!foundLocale) {
			foundLocale = lookupLanguages.find((lLang) => lLang.includes(locale.substr(0, 2)));
		}
		if (foundLocale)
			return foundLocale;
	}
	let browserFallBackLang = navigator.language || navigator.userLanguage;
	browserFallBackLang = adjustLocaleToMatchBE(browserFallBackLang);

	if (browserFallBackLang && lookupLanguages.includes(browserFallBackLang)) {
		return browserFallBackLang;
	} else {
		return adjustLocaleToMatchBE(fallBackLanguage);
	}
}

const supportedLocalesApi = [
	{
		label: <Translate value="app-general-languages_en-US" />,
		value: "en_US",
		supportLink: "en-us",
	},
	{
		label: <Translate value="app-general-languages_fr-CA" />,
		value: "fr_CA",
		supportLink: "fr-ca",
	},
	{
		label: <Translate value="app-general-languages_fr-FR" />,
		value: "fr_FR",
		supportLink: "fr-fr",
	},
	{
		label: <Translate value="app-general-languages_nl-NL" />,
		value: "nl_NL",
		supportLink: "nl-nl",
	},
	{
		label: <Translate value="app-general-languages_es-ES" />,
		value: "es_ES",
		supportLink: "es-es",
	},
	{
		label: <Translate value="app-general-languages_pt-BR" />,
		value: "pt_BR",
		supportLink: "pt-br",
	},
	{
		label: <Translate value="app-general-languages_es-US" />,
		value: "es_US",
		supportLink: "es-us",
	},
	{
		label: <Translate value="app-general-languages_fi" />,
		value: "fi",
		supportLink: "fi",
	},
	{
		label: <Translate value="app-general-languages_sv" />,
		value: "sv",
		alternative: "sv_SE",
		supportLink: "sv",
	},
	{
		label: <Translate value="app-general-languages_it" />,
		value: 'it',
		supportLink: "it",
	},
	{
		label: <Translate value="app-general-languages_el-GR" />,
		value: 'el_GR',
		supportLink: "el-GR",
	},
];

function setLocaleWithFallback(desiredLocale, languageList = undefined) {
	// for the language we have two points of truth, the cookies, and the language state in the store
	// we also need to adjust moment with the proper locale
	window.localStorage.removeItem("billingToken");
	const finalLang = getLocaleWithFallback(desiredLocale, languageList);
	moment.locale(finalLang.replace("_", "-"));
	new Cookies().set("locale", finalLang, { path: "/" });
	store.dispatch(setLanguage(finalLang));
	window.document.querySelector("html").setAttribute("lang", finalLang.replace("_", "-"));
	return (dispatch) => dispatch(setLocale(finalLang));
}

function setDatePickerLocale(language = "en_US") {
	try {
		// eslint-disable-next-line no-undef
		let curLocale = require(`date-fns/locale/${language.replace(
			"_",
			"-"
		)}/index.js`);
		registerLocale(curLocale.code, curLocale);
		setDefaultLocale(curLocale.code);
	} catch (error) {
		try {
			// eslint-disable-next-line no-undef
			let curLocale = require(`date-fns/locale/${language
				.replace("_", "-")
				.substring(0, 2)}/index.js`);
			registerLocale(curLocale.code, curLocale);
			setDefaultLocale(curLocale.code);
		} catch (e) {
			// eslint-disable-next-line no-undef
			let curLocale = require("date-fns/locale/en-US/index.js");
			registerLocale("en-US", curLocale);
			setDefaultLocale("en-US");
		}
	}
}

function setLocalLocize() {
	const urlLocale = getUrlQueryParam("locale");

	let cookies = new Cookies();
	const cookieLocale = cookies.get("locale");
	let finalLocale = "en_US";
	if (urlLocale) {
		finalLocale = urlLocale;
	} else if (cookieLocale) {
		finalLocale = cookieLocale;
	}
	finalLocale = getLocaleWithFallback(finalLocale);

	let translations = {};
	translations[finalLocale] =
		PublicTranslations[finalLocale ? finalLocale.replace("_", "-") : "en-US"];
	syncTranslationWithStore(store);
	store.dispatch(loadTranslations(translations));
	store.dispatch(setLocaleWithFallback(finalLocale));
}

export {
	getLanguagesOptions,
	getLanguageLabel,
	adjustLocaleToMatchBE,
	getLocaleWithFallback,
	languages,
	fallBackLanguage,
	supportedLocalesApi,
	getUrlsBasedOnLanguage,
	setLocaleWithFallback,
	setDatePickerLocale,
	setLocalLocize,
};
